import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Field, FormSection } from '@/components/catalog/CatalogParts';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import {
  useDeleteOnlineAccount,
  useSaveOnlineAccount,
  type OnlineAccountInput,
} from '@/features/onlineAccounts/hooks';
import {
  ONLINE_ACCOUNT_CATEGORIES,
  ONLINE_ACCOUNT_CATEGORY_LABELS,
  ONLINE_ACCOUNT_STATUSES,
  ONLINE_ACCOUNT_STATUS_LABELS,
  friendlyOnlineAccountError,
  type OnlineAccount,
} from '@/features/onlineAccounts/model';
import { useSoftwareSubscriptions } from '@/features/software/hooks';
import type { SeatProfile } from '@/features/software/model';
import type { OnlineAccountCategory, OnlineAccountStatus } from '@/types/database';

const NONE = '__none';
const SOMEONE_ELSE = '__other';

function toInput(account: OnlineAccount | null): OnlineAccountInput {
  return {
    id: account?.id,
    name: account?.name ?? '',
    category: account?.category ?? 'other',
    status: account?.status ?? 'active',
    website: account?.website ?? '',
    subscriptionId: account?.subscription_id ?? null,
    accountNumber: account?.account_number ?? '',
    phone: account?.phone ?? '',
    address: account?.address ?? '',
    openedOn: account?.opened_on ?? '',
    openedById: account?.opened_by ?? null,
    openedByName: account?.opened_by ? '' : (account?.opened_by_name ?? ''),
    notes: account?.notes ?? '',
  };
}

export function OnlineAccountDialog({
  open,
  account,
  people,
  canDelete,
  onOpenChange,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  account: OnlineAccount | null;
  /** Who can be picked as the person who opened the account. */
  people: SeatProfile[];
  canDelete: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (id: string, created: boolean) => void;
  onDeleted?: () => void;
}) {
  const save = useSaveOnlineAccount();
  const remove = useDeleteOnlineAccount();
  const subscriptions = useSoftwareSubscriptions();
  const [draft, setDraft] = useState<OnlineAccountInput>(() => toInput(account));
  const [openedByOther, setOpenedByOther] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(toInput(account));
    setOpenedByOther(!account?.opened_by && !!account?.opened_by_name);
    setConfirmDelete(false);
  }, [open, account]);

  const set = <K extends keyof OnlineAccountInput>(key: K, value: OnlineAccountInput[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const openedBySelect = draft.openedById ?? (openedByOther ? SOMEONE_ELSE : NONE);
  const pending = save.isPending || remove.isPending;
  const subscriptionOptions = (subscriptions.data ?? []).filter(
    (s) => s.status !== 'cancelled' || s.id === draft.subscriptionId,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save
              .mutateAsync(draft)
              .then((id) => {
                toast.success(account ? 'Account updated.' : 'Account added.');
                onOpenChange(false);
                onSaved?.(id, !account);
              })
              .catch((err) => toast.error(friendlyOnlineAccountError(err, 'Could not save.')));
          }}
        >
          <DialogHeader>
            <DialogTitle>{account ? `Edit ${account.name}` : 'Add an account'}</DialogTitle>
            <DialogDescription>
              {account
                ? 'Logins and passwords are edited on the account page.'
                : 'Add the account first, then its admin and user logins. Only super admins see a new account until you share it.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <FormSection title="Account">
              <Field label="Name" htmlFor="account-name" className="sm:col-span-2">
                <Input
                  id="account-name"
                  value={draft.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="e.g. UPS, Instagram, IRS e-Services"
                  required
                />
              </Field>
              <Field label="Category">
                <Select
                  value={draft.category}
                  onValueChange={(value) => set('category', value as OnlineAccountCategory)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ONLINE_ACCOUNT_CATEGORIES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {ONLINE_ACCOUNT_CATEGORY_LABELS[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select
                  value={draft.status}
                  onValueChange={(value) => set('status', value as OnlineAccountStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ONLINE_ACCOUNT_STATUSES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {ONLINE_ACCOUNT_STATUS_LABELS[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Sign-in page" htmlFor="account-website">
                <Input
                  id="account-website"
                  value={draft.website}
                  onChange={(e) => set('website', e.target.value)}
                  placeholder="e.g. ups.com/login"
                  autoComplete="off"
                />
              </Field>
              <Field label="Account number" htmlFor="account-number" hint="If the account has one.">
                <Input
                  id="account-number"
                  value={draft.accountNumber}
                  onChange={(e) => set('accountNumber', e.target.value)}
                  autoComplete="off"
                />
              </Field>
              {subscriptionOptions.length > 0 && (
                <Field
                  label="Subscription"
                  className="sm:col-span-2"
                  hint="Link it if the company pays for this account under Subscriptions."
                >
                  <Select
                    value={draft.subscriptionId ?? NONE}
                    onValueChange={(value) => set('subscriptionId', value === NONE ? null : value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not linked</SelectItem>
                      {subscriptionOptions.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </FormSection>

            <FormSection title="On file with the account" description="Only if the account uses them.">
              <Field label="Phone number" htmlFor="account-phone">
                <PhoneNumberInput
                  id="account-phone"
                  value={draft.phone}
                  onChange={(next) => set('phone', next)}
                  autoComplete="off"
                />
              </Field>
              <Field label="Address" htmlFor="account-address" className="sm:col-span-2">
                <Textarea
                  id="account-address"
                  value={draft.address}
                  onChange={(e) => set('address', e.target.value)}
                  rows={2}
                  placeholder="Street, city, state, ZIP"
                />
              </Field>
            </FormSection>

            <FormSection title="Created">
              <Field label="Created on" htmlFor="account-opened-on" hint="When the account was opened.">
                <Input
                  id="account-opened-on"
                  type="date"
                  value={draft.openedOn}
                  onChange={(e) => set('openedOn', e.target.value)}
                />
              </Field>
              <Field label="Created by">
                <Select
                  value={openedBySelect}
                  onValueChange={(value) => {
                    if (value === SOMEONE_ELSE) {
                      setOpenedByOther(true);
                      set('openedById', null);
                    } else if (value === NONE) {
                      setOpenedByOther(false);
                      set('openedById', null);
                    } else {
                      setOpenedByOther(false);
                      set('openedById', value);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a person" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not recorded</SelectItem>
                    {people.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.full_name || p.email}
                      </SelectItem>
                    ))}
                    <SelectItem value={SOMEONE_ELSE}>Someone not in Atlas…</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {openedBySelect === SOMEONE_ELSE && (
                <Field label="Their name" htmlFor="account-opened-by-name" className="sm:col-span-2">
                  <Input
                    id="account-opened-by-name"
                    value={draft.openedByName}
                    onChange={(e) => set('openedByName', e.target.value)}
                    placeholder="e.g. Former office manager"
                    required
                  />
                </Field>
              )}
            </FormSection>

            <FormSection title="Notes">
              <Field label="Notes" htmlFor="account-notes" className="sm:col-span-2">
                <Textarea
                  id="account-notes"
                  value={draft.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={3}
                  placeholder="What it’s used for, how to reach support. Notes aren’t encrypted, so keep secrets in a login’s password."
                />
              </Field>
            </FormSection>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            {canDelete && account ? (
              confirmDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={pending}
                  onClick={() =>
                    void remove
                      .mutateAsync(account.id)
                      .then(() => {
                        toast.success(`${account.name} deleted.`);
                        onOpenChange(false);
                        onDeleted?.();
                      })
                      .catch((err) =>
                        toast.error(friendlyOnlineAccountError(err, 'Could not delete.')),
                      )
                  }
                >
                  Delete account and its passwords
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending || !draft.name.trim()}>
                {account ? 'Save' : 'Add account'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
